import React from 'react';
import {
  Typography,
  Box,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import AssociatedLawyersCard from '../components/AssociatedLawyersCard';
import {
  Description,
  Assignment,
  Layers,
  Person,
  Add,
  ChevronRight,
  SupportAgent,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import api from '../services/api';
import { stitchColors } from '../theme/tokens';
import { questionnaireService, IQuestionnaire } from '../services/questionnaireService';
import { Chip } from '@mui/material';

interface DashboardStats {
  totalTemplates: number;
  totalWills: number;
  totalAssignedWills: number;
  totalAssignedDocuments: number;
}

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const Dashboard: React.FC = () => {
  const { user } = useAuthStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const isLawyer = user?.userType === 'EL' || ['PRO', 'PLUS'].includes(user?.role || '');

  const { data: questionnaires = [] } = useQuery<IQuestionnaire[]>({
    queryKey: ['questionnaires', user?.id],
    queryFn: () => questionnaireService.getQuestionnaires(),
    enabled: !!isLawyer,
  });

  const activeQuestionnaire = questionnaires.find(q => q.isActive);

  const { data: stats = { totalTemplates: 0, totalWills: 0, totalAssignedWills: 0, totalAssignedDocuments: 0 } } = useQuery<DashboardStats>({
    queryKey: ['dashboardStats', user?.id, user?.role, 'templates,wills,assignedDocuments'],
    queryFn: async () => {
      const response = await api.get<DashboardStats>('/dashboard/stats', {
        params: { types: 'templates,wills,assignedDocuments' },
      });
      return response.data;
    },
    enabled: !!user,
  });

  const totalAssignedDocuments = stats.totalAssignedDocuments ?? stats.totalAssignedWills;

  const filledMilestones = [
    stats.totalTemplates > 0,
    stats.totalWills > 0,
    totalAssignedDocuments > 0,
  ].filter(Boolean).length;
  const estateScore = Math.round((filledMilestones / 3) * 100);

  const summaryRows = [
    {
      title: 'Document History',
      subtitle: `${stats.totalWills} total documents`,
      icon: <Description />,
      accent: stitchColors.secondaryContainer,
      onClick: () => navigate('/document-library'),
    },
    {
      title: 'Active Documents',
      subtitle: `${stats.totalWills} available in your workspace`,
      icon: <Assignment />,
      accent: stitchColors.tertiaryFixedDim,
      onClick: () => navigate('/document-library'),
    },
    {
      title: 'Assigned documents',
      subtitle: `${totalAssignedDocuments} in circulation`,
      icon: <Layers />,
      accent: stitchColors.outlineVariant,
      onClick: () => navigate('/assigned-documents'),
    },
  ];

  return (
    <Box
      sx={{
        maxWidth: 1200,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 3, md: 4 },
        pb: 4,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'flex-end' },
          justifyContent: 'space-between',
          gap: 2,
          borderBottom: `1px solid ${stitchColors.outlineVariant}`,
          pb: 3,
        }}
      >
        <Box>
          <Typography variant="h1" component="h1" sx={{ mb: 1, fontSize: isMobile ? '2rem' : undefined }}>
            {greeting()}, {user?.name?.split(' ')[0] || 'there'}.
          </Typography>
          <Typography sx={{ fontSize: '1.125rem', lineHeight: 1.6, color: stitchColors.onSurfaceVariant }}>
            Your estate workspace is organized under your {user?.role || 'FREE'} plan.{' '}
            {totalAssignedDocuments > 0
              ? `You have ${totalAssignedDocuments} assigned document${totalAssignedDocuments === 1 ? '' : 's'}.`
              : 'Start a document when you are ready.'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<SupportAgent />}
            onClick={() => navigate('/user-lists')}
            sx={{ borderRadius: 1, fontWeight: 600 }}
          >
            Users
          </Button>
          {user?.userType === 'EO' && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => navigate('/document-builder')}
              sx={{ borderRadius: 1, fontWeight: 600, boxShadow: '0 1px 2px rgba(18,28,40,0.08)' }}
            >
              Document Builder
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              border: `1px solid ${stitchColors.outlineVariant}`,
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: stitchColors.surfaceContainerLowest,
            }}
          >
            <Box
              sx={{
                bgcolor: stitchColors.surface,
                px: 2,
                py: 2,
                borderBottom: `1px solid ${stitchColors.outlineVariant}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="h3" component="h2">
                Workspace overview
              </Typography>
              <Button size="small" onClick={() => navigate('/')} sx={{ fontWeight: 600, textTransform: 'none' }}>
                Refresh
              </Button>
            </Box>
            <Box sx={{ p: 3 }}>
              {summaryRows.map((row) => (
                <Box
                  key={row.title}
                  onClick={row.onClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      row.onClick();
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    mb: 2,
                    border: `1px solid ${stitchColors.outlineVariant}`,
                    borderRadius: 1.5,
                    bgcolor: stitchColors.surfaceContainerLowest,
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'border-color 0.2s ease',
                    '&:last-of-type': { mb: 0 },
                    '&:hover': {
                      borderColor: `${stitchColors.primary}80`,
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      bgcolor: row.accent,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        bgcolor: stitchColors.surfaceVariant,
                        color: stitchColors.onSurfaceVariant,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '& .MuiSvgIcon-root': { fontSize: 22 },
                      }}
                    >
                      {row.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: stitchColors.onBackground }}>{row.title}</Typography>
                      <Typography sx={{ fontSize: '0.875rem', color: stitchColors.onSurfaceVariant, mt: 0.25 }}>
                        {row.subtitle}
                      </Typography>
                    </Box>
                  </Box>
                  <ChevronRight sx={{ color: stitchColors.outline }} />
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 2,
                bgcolor: stitchColors.primary,
                color: stitchColors.onPrimary,
                p: 3,
                border: `1px solid ${stitchColors.primaryContainer}`,
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -32,
                  right: -32,
                  width: 128,
                  height: 128,
                  borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.08)',
                  filter: 'blur(24px)',
                  pointerEvents: 'none',
                }}
              />
              <Typography variant="h3" component="h2" sx={{ color: stitchColors.onPrimary, mb: 1, position: 'relative' }}>
                Estate readiness
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 2, position: 'relative' }}>
                <Typography
                  sx={{
                    fontFamily: theme.typography.h2.fontFamily,
                    fontSize: '2.25rem',
                    fontWeight: 600,
                    lineHeight: 1,
                  }}
                >
                  {estateScore}%
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: stitchColors.primaryFixedDim, pb: 0.5 }}>
                  From workspace activity
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  color: stitchColors.primaryFixed,
                  mb: 3,
                  position: 'relative',
                }}
              >
                Build templates, create wills, and share assigned documents to reach a complete profile.
              </Typography>
              <Box sx={{ height: 8, borderRadius: 4, bgcolor: stitchColors.primaryContainer, position: 'relative' }}>
                <Box
                  sx={{
                    height: '100%',
                    width: `${estateScore}%`,
                    borderRadius: 4,
                    bgcolor: stitchColors.tertiaryFixed,
                    transition: 'width 0.4s ease',
                  }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                border: `1px solid ${stitchColors.outlineVariant}`,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: stitchColors.surfaceContainerLowest,
                flex: 1,
              }}
            >
              <Box
                sx={{
                  bgcolor: stitchColors.surface,
                  px: 2,
                  py: 2,
                  borderBottom: `1px solid ${stitchColors.outlineVariant}`,
                }}
              >
                <Typography variant="h3" component="h2" sx={{ fontSize: '1.125rem' }}>
                  Plan &amp; profile
                </Typography>
              </Box>
              <Box sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: stitchColors.surfaceTint,
                      color: stitchColors.onPrimary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                    }}
                  >
                    {user?.name?.charAt(0) || 'U'}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>{user?.name}</Typography>
                    <Typography sx={{ fontSize: '0.875rem', color: stitchColors.onSurfaceVariant }}>
                      <Person sx={{ fontSize: 14, verticalAlign: 'text-bottom', mr: 0.5 }} />
                      {user?.role} plan
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.8125rem', color: stitchColors.onSurfaceVariant, lineHeight: 1.6 }}>
                  Australian Wills keeps navigation and permissions aligned with your subscription. Use the sidebar to open
                  builders, libraries, and assigned work.
                </Typography>
              </Box>
            </Box>

            {/* EO-only: show a summary of associated lawyers */}
            {user?.userType === 'EO' && <AssociatedLawyersCard />}

            {/* Lawyer-only: show a summary of the active welcome questionnaire */}
            {isLawyer && (
              <Box
                sx={{
                  border: `1px solid ${activeQuestionnaire ? '#a7f3d0' : stitchColors.outlineVariant}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: activeQuestionnaire ? '#f0fdf4' : stitchColors.surfaceContainerLowest,
                }}
              >
                <Box
                  sx={{
                    bgcolor: activeQuestionnaire ? '#ecfdf5' : stitchColors.surface,
                    px: 2,
                    py: 2,
                    borderBottom: `1px solid ${activeQuestionnaire ? '#a7f3d0' : stitchColors.outlineVariant}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="h3" component="h2" sx={{ fontSize: '1.125rem' }}>
                    Welcome Questionnaire
                  </Typography>
                  <Chip
                    label={activeQuestionnaire?.isSystemDefault ? 'DEFAULT' : (activeQuestionnaire ? 'ACTIVE' : 'GLOBAL')}
                    color={activeQuestionnaire ? 'success' : 'default'}
                    size="small"
                    sx={{ fontWeight: 700, fontSize: '0.6875rem' }}
                  />
                </Box>
                <Box sx={{ p: 2.5 }}>
                  <Typography sx={{ fontWeight: 600, color: stitchColors.onBackground, mb: 0.5 }}>
                    {activeQuestionnaire ? activeQuestionnaire.title : 'Global Welcome Questionnaire'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8125rem', color: stitchColors.onSurfaceVariant, lineHeight: 1.5, mb: 2 }}>
                    {activeQuestionnaire?.isSystemDefault
                      ? 'The platform default questionnaire configured by Super Admin is active for your clients.'
                      : activeQuestionnaire
                        ? 'Clients are required to complete this questionnaire when they log in.'
                        : 'No custom questionnaire is active. Clients see the platform default questionnaire.'}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    onClick={() => navigate('/questionnaire-library')}
                    sx={{ borderRadius: 1, fontWeight: 600 }}
                  >
                    Manage Questionnaires
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
